/**
 * MobileBottomNav — Bottom tab bar for mobile/tablet.
 * Shows icons + labels, theme toggle, and "More" overflow.
 *
 * ponytail: only renders at ≤768px via CSS display:none on desktop.
 * Requirements: 2.1, 3.2, 3.3, 6.1
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import './MobileBottomNav.css';

const navItems = [
  { id: 'search', label: 'Search', path: '/search/station', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" /><path d="M20 20L16 16" />
    </svg>
  )},
  { id: 'availability', label: 'Availability', path: '/availability', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3V21H21" /><path d="M7 16L12 11L15 14L21 8" />
    </svg>
  )},
  { id: 'observations', label: 'Observations', path: '/observations', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2V5M12 19V22M2 12H5M19 12H22" />
    </svg>
  )},
  { id: 'statistics', label: 'Stats', path: '/statistics', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="13" width="4" height="8" rx="1" /><rect x="10" y="8" width="4" height="13" rx="1" /><rect x="17" y="3" width="4" height="18" rx="1" />
    </svg>
  )},
] as const;

// ponytail: theme cycle lives inline — no separate component needed
const SunIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2V4M12 20V22M4 12H2M22 12H20M5.64 5.64L4.22 4.22M19.78 19.78L18.36 18.36M5.64 18.36L4.22 19.78M19.78 4.22L18.36 5.64" />
  </svg>
);
const MoonIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" />
  </svg>
);
const GearIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

export const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setActiveSection } = useAppStore();
  const [moreOpen, setMoreOpen] = useState(false);

  const getActiveId = () => {
    const path = location.pathname;
    if (path.startsWith('/search')) return 'search';
    if (path.startsWith('/availability')) return 'availability';
    if (path.startsWith('/observations')) return 'observations';
    if (path.startsWith('/statistics')) return 'statistics';
    if (path.startsWith('/station-quality')) return 'more';
    if (path.startsWith('/network')) return 'more';
    return 'search';
  };

  const activeId = getActiveId();

  const handleTap = (item: typeof navItems[number]) => {
    setMoreOpen(false);
    setActiveSection(item.id as any);
    navigate(item.path);
  };

  const cycleTheme = () => {
    const root = document.documentElement;
    const current = root.getAttribute('data-theme') || localStorage.getItem('stationExplorer_theme') || 'system';
    const next = current === 'dark' ? 'light' : current === 'light' ? 'system' : 'dark';
    if (next === 'system') {
      root.removeAttribute('data-theme');
      root.style.colorScheme = '';
    } else {
      root.setAttribute('data-theme', next);
      root.style.colorScheme = next;
    }
    localStorage.setItem('stationExplorer_theme', next);
  };

  const currentTheme = (): string => {
    if (typeof document === 'undefined') return 'system';
    return document.documentElement.getAttribute('data-theme') || localStorage.getItem('stationExplorer_theme') || 'system';
  };

  return (
    <nav className="mobile-bottom-nav" aria-label="Main navigation">
      {/* More menu popover */}
      {moreOpen && (
        <div className="mobile-more-menu" role="menu">
          <button className="mobile-more-item" role="menuitem" onClick={() => { setMoreOpen(false); navigate('/network'); }}>
            Network
          </button>
          <button className="mobile-more-item" role="menuitem" onClick={() => { setMoreOpen(false); navigate('/station-quality'); }}>
            Quality
          </button>
        </div>
      )}

      {navItems.map((item) => (
        <button
          key={item.id}
          className={`mobile-nav-item ${activeId === item.id ? 'active' : ''}`}
          onClick={() => handleTap(item)}
          aria-label={item.label}
          aria-current={activeId === item.id ? 'page' : undefined}
        >
          <span className="mobile-nav-icon">{item.icon}</span>
          <span className="mobile-nav-label">{item.label}</span>
        </button>
      ))}

      {/* Theme toggle */}
      <button
        className="mobile-nav-item"
        onClick={cycleTheme}
        aria-label={`Theme: ${currentTheme()}. Tap to change.`}
      >
        <span className="mobile-nav-icon">
          {currentTheme() === 'dark' ? <MoonIcon /> : <SunIcon />}
        </span>
        <span className="mobile-nav-label">Theme</span>
      </button>

      {/* More — overflow nav */}
      <button
        className={`mobile-nav-item ${activeId === 'more' ? 'active' : ''}`}
        onClick={() => setMoreOpen(!moreOpen)}
        aria-label="More options"
        aria-expanded={moreOpen}
        aria-haspopup="menu"
      >
        <span className="mobile-nav-icon"><GearIcon /></span>
        <span className="mobile-nav-label">More</span>
      </button>
    </nav>
  );
};
